<!--suppress ALL -->
<template>
	<div id="video-preview">
		<loading-icon></loading-icon>
		<p class="loading-message">
			We are creating your custom video. This may take a few minutes.
		</p>
	</div>
</template>

<script>
import LoadingIcon from '@/components/global/LoadingIcon.vue';
import { mapState, mapActions, mapGetters } from 'vuex';
import * as htmlToImage from 'html-to-image';
import apiClient from '@/services/api-client';
import { ERROR_DETAILS } from '@/constants/error-details';

export default {
	name: 'BbPreview-VideoPreview',
	components: {
		LoadingIcon,
	},
	data() {
		return {
			targetScale: null,
			interval: null,
			finalVideoUrl: null,
			height: null,
			width: null,
		};
	},
	computed: {
		...mapState({
			completeVideo: (state) => state.brandBuilderEditor.completeVideo,
			videoIsVertical: (state) => state.brandBuilderEditor.videoIsVertical,
		}),
		...mapGetters(['selectedVideo']),
	},
	methods: {
		...mapActions(['completeVideoAction', 'showErrorModal']),
		handleVideoCreationFailure() {
			this.showErrorModal({
				error: 'Video Creation Error',
				errorDetails: {
					...ERROR_DETAILS.VIDEO_ERROR,
					message:
						'An error occurred while creating your video. Please try again. If the issue continues, please contact your Account Executive.',
				},
			});
		},
		transformMedia(
			baseVideo,
			encodedMedia,
			startOffset,
			endOffset,
			x,
			y,
			width,
			height,
		) {
			return apiClient()
				.post('/TransformMedia/TransformMedia', {
					baseVideo,
					encodedMedia,
					startOffset,
					endOffset,
					x,
					y,
					width,
					height,
				})
				.then((response) => {
					this.completeVideoAction(
						response.data.transformUrl.replace('http://', 'https://'),
					);
					document.getElementById(
						'video-preview',
					).innerHTML = `<video controls autoplay width="100%" poster="${
						this.selectedVideo.previewUri
					}"
				style="${
					this.videoIsVertical
						? 'max-height: 960px; min-height: 340px; width: auto;'
						: 'max-width: 900px; min-width: 320px;'
				}">
                        <source src="${this.completeVideo}">

                        Sorry, your browser doesn't support embedded videos.
					</video>`;
				})
				.catch(() => {
					// eslint-disable-next-line no-console
					this.handleVideoCreationFailure();
				});
		},
		makeVideo(videoToTransform, start, stop) {
			/* baseVideo,
				mediaByteArray,
				startOffset,
				endOffset,
				transformUrl, */
			htmlToImage
				.toPng(document.getElementById('item-container'), {
					backgroundColor: '#fff',
					quality: 1,
					opacity: 1,
					pixelRatio: 1,
				})
				.then((dataURI) => {
					const strippedString = dataURI.replace('data:image/png;base64,', '');
					this.transformMedia(
						videoToTransform,
						strippedString,
						start,
						stop,
						0,
						0,
						this.width,
						this.height,
					);
				})
				.catch((error) => {
					// eslint-disable-next-line no-console
					this.handleVideoCreationFailure();
				});
		},
		makeVideoCall() {
			// This allows the Title card scale to be a scale of 1 instead of the viewport size.
			document.getElementById('item-container').classList.add('fullscale');
			// This gets the current scale of the title card.
			const compStyle = window.getComputedStyle(this.targetScale);
			const transformValue = compStyle.getPropertyValue('transform');
			// If the interval does not produce the scale of 1 result redo the interval and once the scale of 1 is confirmed proceed to the htmlToImage checks
			if (transformValue !== 'matrix(1, 0, 0, 1, 0, 0)') {
				return;
			}
			clearInterval(this.interval);
			// Now that the scale is correct, create the stitching for the video
			this.makeVideo(
				this.finalVideoUrl,
				this.selectedVideo.mediaTransform.startOffset,
				this.selectedVideo.mediaTransform.endOffSet,
			);
		},
	},
	mounted() {
		if (this.completeVideo) {
			document.getElementById(
				'video-preview',
			).innerHTML = `<video controls autoplay width="100%" :poster="${
				this.selectedVideo.previewUri
			}"
			style="${
				this.videoIsVertical
					? 'max-height: 960px; min-height: 340px; width: auto;'
					: 'max-width: 900px; min-width: 320px;'
			}">
				<source src="${this.completeVideo}">
				Sorry, your browser doesn't support embedded videos.
			</video>`;
		} else {
			const startUrl = this.selectedVideo.uri.replace(
				'https://res.cloudinary.com/uwm/video/upload/',
				'',
			);
			const regex = /[a-zA-Z0-9]*/;
			this.finalVideoUrl = startUrl.replace(regex, '');

			// create the target item for the scale check
			this.targetScale = document.getElementById('item-container');
			this.height = 1080;
			this.width = 1920;
			if (this.videoIsVertical) {
				this.height = 1920;
				this.width = 1080;
			}
			// Set the target check to an interval
			this.interval = setInterval(this.makeVideoCall, 250);
		}
	},
	destroyed() {
		// This allows the Title card to return the scale to the viewport size needed.
		document.getElementById('item-container').classList.remove('fullscale');
	},
};
</script>

<style lang="scss" scoped>
#video-preview {
	background-color: $white;
	z-index: 1000;
	width: calc(100vw - #{$brand-builder-width + ($border-width * 2)});
	height: calc(100vh - #{$top-nav-height});

	@include sm {
		width: 100%;
		height: calc(
			100vh - (#{$top-nav-height + 1px + $brand-builder-tool-height + 59px})
		); /* This excludes the Top Nav, Brand Builder Nav and Footer Height 230px for under 1024px width*/
		@include rem(padding, 20px);
		left: 0;
	}

	@include tablet-and-below {
		width: calc(100vw - (#{$base-gap} * 2));
		height: calc(
			100vh -
				(
					(#{$top-nav-height} + 1px) + #{$brand-builder-tool-height} + #{$brand-builder-mobile-footer}
				)
		); /* This excludes the Top Nav, Brand Builder Nav and Footer Height 248px */
		padding: unset;
		left: unset;
	}

	@include mobile {
		width: calc(100vw - (#{$base-gap-mobile} * 2));
	}

	display: flex;
	justify-content: center;
	position: fixed;
	top: $top-nav-height + $border-width;
	left: $brand-builder-width + ($border-width * 2);
	box-sizing: border-box;
	flex-direction: column;
	align-items: center;

	#item-container {
		margin: $base-padding;
	}

	.loading-message {
		@include rem(margin-bottom, 25px);
		@include rem(font-size, 16px);
		@include typo--roboto__regular;
		color: $subheader-color;

		@include tablet-and-below {
			text-align: center;
		}
	}
}
</style>
